/**
 * Commercetools cart update action builders for external tax rates and custom fields.
 */

import {
  TAX_FIELD_MAPPING,
  ACTION_SET_LINE_ITEM_TAX_RATE,
  ACTION_SET_LINE_ITEM_CUSTOM_FIELD,
  EXTERNAL_TAX_RATE_NAME,
} from '../constants/tax.constants';
import type { Cart, NormalizedTaxResult } from '../types/tax.types';

// ─── Build CT Tax Rate Actions ────────────────────────────────────────

export const buildTaxedPriceActions = (
  taxSummary: NormalizedTaxResult | null | undefined,
  currentCart: Cart,
  preferredCountry?: string
) => {
  if (!taxSummary?.lineItems?.length) return [];

  const cartLineItems = currentCart?.lineItems ?? [];
  const country = preferredCountry || currentCart?.shippingAddress?.country || currentCart?.country || '';

  const actions: Array<{
    action: typeof ACTION_SET_LINE_ITEM_TAX_RATE;
    lineItemId: string;
    externalTaxRate: {
      name: string;
      amount: number;
      country: string;
      includedInPrice: boolean;
    };
  }> = [];

  for (let idx = 0; idx < cartLineItems.length; idx++) {
    const cartItem = cartLineItems[idx];
    if (!cartItem || idx >= taxSummary.lineItems.length) continue;

    actions.push({
      action: ACTION_SET_LINE_ITEM_TAX_RATE,
      lineItemId: cartItem.id,
      externalTaxRate: {
        name: EXTERNAL_TAX_RATE_NAME,
        amount: taxSummary.lineItems[idx].priceData?.taxRate ?? 0,
        country,
        includedInPrice: false,
      },
    });
  }

  return actions;
};

// ─── Build CT Custom Field Actions ────────────────────────────────────

export const buildTaxCustomFieldActions = (
  taxSummary: NormalizedTaxResult | null | undefined,
  currentCart: Cart
) => {
  if (!taxSummary?.lineItems?.length) return [];

  const cartLineItems = currentCart?.lineItems ?? [];
  const actions: Array<{
    action: typeof ACTION_SET_LINE_ITEM_CUSTOM_FIELD;
    lineItemId: string;
    name: string;
    value: string;
  }> = [];

  cartLineItems.forEach((cartItem, idx) => {
    if (!cartItem || idx >= taxSummary.lineItems.length) return;
    const summaryEntries = taxSummary.lineItems[idx]?.priceData?.taxSummary;
    if (!Array.isArray(summaryEntries) || summaryEntries.length === 0) return;

    // Use last entry (Ship-To / destination-based authority)
    const entry = summaryEntries[summaryEntries.length - 1] as unknown as Record<string, unknown>;
    Object.entries(TAX_FIELD_MAPPING).forEach(([apiField, customField]) => {
      actions.push({
        action: ACTION_SET_LINE_ITEM_CUSTOM_FIELD,
        lineItemId: cartItem.id,
        name: customField,
        value: (entry[apiField] as string) ?? '',
      });
    });
  });

  return actions;
};
